import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import ItemsLoading from '../../../../components/Loaders/ItemsLoading'
import FacebookAdCardListItem from './FacebookAdCardListItem'
import SkeletonFacebookAdCardListItem from './FacebookAdCardListItem/Skeleton'
import PageSection from '../../../../features/components/PageSection'
import Can from '../../../../features/components/Can'
import AdFacebookEdit from '../../../../forms/Facebook/AdForms/AdFacebookEdit'
import DisapproveInternalAdModal from '../../../AdApprovals/DisapproveInternalAdModal'

import { useFacebookMediaLoading } from './useFacebookMediaLoading'
import useModalManage from '../../../../hooks/useModalManage'
import { getAllImageHashesFromAdsList } from './helpers'

import { getSelectedPlatform } from '../../../../helpers/handlePlatformSelection'

import { clearAccessibleSocialPages, getAccessibleSocialPages } from '../../../../modules/actions/socialAccounts'
import { clearFacebookImageUrls, clearFacebookVideoUrls } from '../../../../modules/actions/mediaFiles'
import { clearChoicesCTA, getChoicesCTA } from '../../../../modules/actions/choices'
import { clearAdPreview } from '../../../../modules/actions/ads'
import {
  adsErrorSelector,
  adsIsLoadingSelector,
  adsNextSelector,
  adsWasLoadedSelector
} from '../../../../modules/selectors/ads'
import {
  accessibleSocialPagesErrorSelector,
  accessibleSocialPagesWasLoadedSelector
} from '../../../../modules/selectors/socialAccounts'
import { selectedAdAccountsSelector } from '../../../../modules/selectors/adAccounts'
import { lineItemWasLoadedSelector } from '../../../../modules/selectors/lineItems'
import { campaignWasLoadedSelector } from '../../../../modules/selectors/campaigns'
import { choicesCTAWasLoadedSelector } from '../../../../modules/selectors/choices'
import { selectedPartnershipSelector } from '../../../../modules/selectors/partnerships'

import { FACEBOOK_PROVIDER_PLATFORM } from '../../../../constants/selectLists/platformList'

import useStyles from './styles'

const FacebookAdsPreviewList = ({
  adsList,
  isLineItemSummaryPage,
  loadMoreAdsHandler,
  noDataText,
  showStatus,
  showDetails,
  showMetrics,
  showActionsDropdown,
  showActionsButtons,
  checkPlacements = true,
  isAdApprovalsPage,
  handleDeleteRequest
}) => {
  const classes = useStyles()

  const dispatch = useDispatch()
  const selectedPlatform = getSelectedPlatform()

  const {
    isModalOpened: isDisapproveAdModalOpened,
    openModalHandler: disapproveAdOpenModalHandler,
    closeModalHandler: disapproveAdCloseModalHandler,
    modalData: disapproveAdModalData
  } = useModalManage({})

  const { [selectedPlatform]: selectedAdAccount = {} } = useSelector(selectedAdAccountsSelector)
  const lineItemWasLoaded = useSelector(lineItemWasLoadedSelector)
  const campaignWasLoaded = useSelector(campaignWasLoadedSelector)
  const facebookPagesWasLoaded = useSelector(accessibleSocialPagesWasLoadedSelector)
  const facebookPagesError = useSelector(accessibleSocialPagesErrorSelector)
  const adsNext = useSelector(adsNextSelector)
  const choicesCTAWasLoaded = useSelector(choicesCTAWasLoadedSelector)
  const { rights_holder } = useSelector(selectedPartnershipSelector)

  const { currency_symbol: currencySymbol } = selectedAdAccount

  const imageHashes = useMemo(() => {
    return getAllImageHashesFromAdsList(adsList)
  }, [adsList])

  const { initialMediaIsLoading } = useFacebookMediaLoading({ imageHashes })

  // show metric if at least one item has conversions value
  const showConversionsMetric = useMemo(() => !!adsList.find(ad => ad.conversions), [adsList])
  const skeletonProps = useMemo(
    () => ({ showStatus, showDetails, showMetrics, showActionsButtons, isAdApprovalsStyle: isAdApprovalsPage }),
    [showStatus, showDetails, showMetrics, showActionsButtons, isAdApprovalsPage]
  )

  // still show skeletons until all data for line item summary page is loaded
  // apply this condition only if we're on line item summary page
  const lineItemSummaryPageDataIsLoading = !lineItemWasLoaded || !campaignWasLoaded

  // check that all needed data for ad previews is loaded and allow pages to be loaded with error
  const adPreviewDataIsLoading = !(facebookPagesWasLoaded || facebookPagesError) || initialMediaIsLoading

  const additionalDataIsLoading = useMemo(
    () => adPreviewDataIsLoading || !choicesCTAWasLoaded || (isLineItemSummaryPage && lineItemSummaryPageDataIsLoading),
    [adPreviewDataIsLoading, isLineItemSummaryPage, choicesCTAWasLoaded, lineItemSummaryPageDataIsLoading]
  )

  const renderedAdsListCards = useMemo(
    () =>
      adsList.map(ad => {
        const { id } = ad

        return (
          <FacebookAdCardListItem
            key={id}
            ad={ad}
            currencySymbol={currencySymbol}
            showStatus={showStatus}
            showDetails={showDetails}
            showMetrics={showMetrics}
            showActionsDropdown={showActionsDropdown}
            showActionsButtons={showActionsButtons}
            showConversionsMetric={showConversionsMetric}
            checkPlacements={checkPlacements}
            isLineItemSummaryPage={isLineItemSummaryPage}
            isAdApprovalsStyle={isAdApprovalsPage}
            disapproveInternalAdHandler={disapproveAdOpenModalHandler}
            handleDeleteRequest={handleDeleteRequest}
          />
        )
      }),
    [
      adsList,
      currencySymbol,
      showStatus,
      showDetails,
      showMetrics,
      showActionsDropdown,
      showActionsButtons,
      showConversionsMetric,
      checkPlacements,
      isLineItemSummaryPage,
      isAdApprovalsPage,
      disapproveAdOpenModalHandler,
      handleDeleteRequest
    ]
  )

  useEffect(() => {
    dispatch(getChoicesCTA())
  }, [dispatch])

  useEffect(() => {
    dispatch(
      getAccessibleSocialPages({
        ...(isAdApprovalsPage ? { rightsHolderAccountId: rights_holder } : {}),
        platform: FACEBOOK_PROVIDER_PLATFORM
      })
    )

    return () => {
      dispatch(clearAccessibleSocialPages())
      dispatch(clearFacebookImageUrls())
      dispatch(clearFacebookVideoUrls())
      dispatch(clearAdPreview())
      dispatch(clearChoicesCTA())
    }
  }, [dispatch, isAdApprovalsPage, rights_holder])

  return (
    <>
      <ItemsLoading
        itemsLength={adsList.length}
        className={classes.cardsList}
        noDataText={noDataText}
        isLoadingSelector={adsIsLoadingSelector}
        wasLoadedSelector={adsWasLoadedSelector}
        additionalDataIsLoading={additionalDataIsLoading}
        errorSelector={adsErrorSelector}
        initialLoadingSkeletonsNumber={5}
        SkeletonComponent={SkeletonFacebookAdCardListItem}
        ContentWrapper={PageSection}
        skeletonProps={skeletonProps}
        loadMore={adsNext ? loadMoreAdsHandler : null}
      >
        {renderedAdsListCards}
      </ItemsLoading>
      <Can I="update" a="Ad">
        <AdFacebookEdit shouldBeCleared checkPlacements={checkPlacements} isAdApprovalsPage={isAdApprovalsPage} />
      </Can>
      {/*if show actions buttons, show disapprove modal as well*/}
      {showActionsButtons && (
        <DisapproveInternalAdModal
          isModalOpened={isDisapproveAdModalOpened}
          modalData={disapproveAdModalData}
          onClose={disapproveAdCloseModalHandler}
        />
      )}
    </>
  )
}

FacebookAdsPreviewList.propTypes = {
  adsList: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLineItemSummaryPage: PropTypes.bool,
  loadMoreAdsHandler: PropTypes.func,
  noDataText: PropTypes.string,
  showStatus: PropTypes.bool,
  showDetails: PropTypes.bool,
  showMetrics: PropTypes.bool,
  showActionsDropdown: PropTypes.bool,
  showActionsButtons: PropTypes.bool,
  shouldLoadAllCTA: PropTypes.bool,
  checkPlacements: PropTypes.bool,
  isAdApprovalsPage: PropTypes.bool
}

export default FacebookAdsPreviewList
